/**
 * SERVICIO DE AUTENTICACIÓN CON SUPABASE AUTH
 * 
 * Usa la autenticación nativa de Supabase para:
 * - Registro de usuarios
 * - Login con email/password
 * - Gestión de sesiones automática
 * - Logout y limpieza de sesiones
 */

import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { User } from '../types';
import { SupabaseService } from './supabaseService';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  userType: 'entrepreneur' | 'investor';
}

export interface AuthResult {
  success: boolean;
  user?: User;
  error?: string;
}

/**
 * Clase para manejar autenticación con Supabase Auth
 */
export class AuthService {
  
  /**
   * Registra un nuevo usuario usando Supabase Auth
   */
  static async register(registerData: RegisterData): Promise<AuthResult> {
    try {
      console.log('📝 Iniciando registro con Supabase Auth:', { 
        email: registerData.email, 
        name: registerData.name,
        userType: registerData.userType 
      });

      // Validar datos de entrada
      const validation = this.validateRegisterData(registerData);
      if (!validation.isValid) {
        return { success: false, error: validation.error };
      }

      if (!isSupabaseConfigured()) {
        return this.handleLocalRegistration(registerData);
      }

      // Registrar usuario con Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: registerData.email,
        password: registerData.password,
        options: {
          emailRedirectTo: undefined, // Deshabilitar confirmación por email
          data: {
            email_confirm: false
          }
        }
      });

      if (authError) {
        console.error('❌ Error en registro de Supabase Auth:', authError);
        return { success: false, error: authError.message };
      }

      if (!authData.user) {
        return { success: false, error: 'No se pudo crear el usuario' };
      }

      console.log('✅ Usuario registrado en Supabase Auth:', authData.user.id);

      // Crear perfil completo usando el ID de Supabase Auth
      const newUser = this.createUserFromRegisterData(registerData, authData.user.id);
      
      // Guardar perfil en la tabla users
      const userResult = await SupabaseService.saveUser(newUser);
      if (!userResult.success) {
        console.error('❌ Error al guardar perfil:', userResult.error);
        // Intentar eliminar el usuario de auth si falló el perfil
        await supabase.auth.admin.deleteUser(authData.user.id);
        return { success: false, error: userResult.error };
      }

      console.log('✅ Usuario registrado exitosamente');
      
      // Guardar usuario en localStorage para acceso inmediato
      localStorage.setItem('racksMatchUserProfile', JSON.stringify(newUser));
      
      return {
        success: true,
        user: newUser
      };

    } catch (error) {
      console.error('❌ Error en registro:', error);
      return {
        success: false,
        error: `Error de registro: ${error instanceof Error ? error.message : 'Error desconocido'}`
      };
    }
  }

  /**
   * Inicia sesión usando Supabase Auth
   */
  static async login(credentials: LoginCredentials): Promise<AuthResult> {
    try {
      console.log('🔐 Iniciando login con Supabase Auth:', { email: credentials.email });

      // Validar credenciales
      const validation = this.validateLoginCredentials(credentials);
      if (!validation.isValid) {
        return { success: false, error: validation.error };
      }

      if (!isSupabaseConfigured()) {
        return this.handleLocalLogin(credentials);
      }

      // Login con Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password
      });

      if (authError) {
        console.error('❌ Error en login de Supabase Auth:', authError);
        return { success: false, error: 'Email o contraseña incorrectos' };
      }

      if (!authData.user) {
        return { success: false, error: 'No se pudo iniciar sesión' };
      }

      console.log('✅ Login exitoso en Supabase Auth:', authData.user.id);

      // Obtener perfil completo del usuario
      const userResult = await SupabaseService.getUserById(authData.user.id);
      if (!userResult.success || !userResult.user) {
        return { success: false, error: 'Error al obtener perfil del usuario' };
      }

      // Actualizar last_login
      await supabase
        .from('users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', authData.user.id);

      console.log('✅ Login completo exitoso');
      
      // Guardar en localStorage
      localStorage.setItem('racksMatchUserProfile', JSON.stringify(userResult.user));
      
      return {
        success: true,
        user: userResult.user
      };

    } catch (error) {
      console.error('❌ Error en login:', error);
      return {
        success: false,
        error: `Error de login: ${error instanceof Error ? error.message : 'Error desconocido'}`
      };
    }
  }

  /**
   * Cierra sesión usando Supabase Auth
   */
  static async logout(): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🚪 Cerrando sesión...');

      if (isSupabaseConfigured()) {
        // Cerrar sesión en Supabase Auth
        const { error } = await supabase.auth.signOut();
        if (error) {
          // Si la sesión ya no existe, es normal - solo registrar como advertencia
          if (error.message.includes('Auth session missing') || 
              error.message.includes('Session from session_id claim in JWT does not exist') ||
              error.message.includes('session_not_found')) {
            console.warn('⚠️ Sesión ya expirada o inexistente en Supabase:', error.message);
          } else {
            console.error('❌ Error al cerrar sesión en Supabase:', error);
            // Para errores genuinos de logout (no relacionados con sesión faltante)
            return { success: false, error: `Error al cerrar sesión: ${error.message}` };
          }
        }
      }

      // Limpiar localStorage
      localStorage.removeItem('currentUser');
      localStorage.removeItem('racksMatchUserProfile');

      console.log('✅ Sesión cerrada exitosamente');
      return { success: true };

    } catch (error) {
      console.error('❌ Error al cerrar sesión:', error);
      return { success: false, error: 'Error al cerrar sesión' };
    }
  }

  /**
   * Verifica si hay una sesión válida usando Supabase Auth
   */
  static async verifySession(): Promise<AuthResult> {
    try {
      if (!isSupabaseConfigured()) {
        // Verificar sesión local
        const localUser = localStorage.getItem('racksMatchUserProfile');
        if (localUser) {
          return { success: true, user: JSON.parse(localUser) };
        }
        return { success: false, error: 'No hay sesión local' };
      }

      // Verificar sesión en Supabase Auth
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('❌ Error al verificar sesión:', error);
        return { success: false, error: error.message };
      }

      if (!session || !session.user) {
        return { success: false, error: 'No hay sesión activa' };
      }

      console.log('✅ Sesión válida encontrada:', session.user.id);

      // Obtener perfil completo del usuario
      const userResult = await SupabaseService.getUserById(session.user.id);
      if (!userResult.success || !userResult.user) {
        return { success: false, error: 'Error al obtener perfil del usuario' };
      }

      return {
        success: true,
        user: userResult.user
      };

    } catch (error) {
      console.error('❌ Error inesperado al verificar sesión:', error);
      return { success: false, error: 'Error de verificación' };
    }
  }

  /**
   * Validaciones
   */
  private static validateRegisterData(data: RegisterData): { isValid: boolean; error?: string } {
    if (!data.name.trim()) return { isValid: false, error: 'El nombre es obligatorio' };
    if (data.name.length < 2) return { isValid: false, error: 'El nombre debe tener al menos 2 caracteres' };
    if (!data.email.trim()) return { isValid: false, error: 'El email es obligatorio' };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return { isValid: false, error: 'Email inválido' };
    if (!data.password) return { isValid: false, error: 'La contraseña es obligatoria' };
    if (data.password.length < 6) return { isValid: false, error: 'La contraseña debe tener al menos 6 caracteres' };
    if (!data.userType) return { isValid: false, error: 'Debes seleccionar el tipo de usuario' };
    
    return { isValid: true };
  }

  private static validateLoginCredentials(credentials: LoginCredentials): { isValid: boolean; error?: string } {
    if (!credentials.email.trim()) return { isValid: false, error: 'El email es obligatorio' };
    if (!credentials.password) return { isValid: false, error: 'La contraseña es obligatoria' };
    
    return { isValid: true };
  }

  /**
   * Crea un usuario completo desde datos de registro usando ID de Supabase Auth
   */
  private static createUserFromRegisterData(registerData: RegisterData, authUserId: string): User {
    const age = Math.floor(Math.random() * (50 - 25 + 1)) + 25;
    
    const locations = [
      'Madrid, España', 'Barcelona, España', 'Valencia, España', 'Sevilla, España',
      'Bilbao, España', 'Zaragoza, España', 'Málaga, España', 'Las Palmas, España'
    ];
    const location = locations[Math.floor(Math.random() * locations.length)];

    const defaultImages = [
      'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop',
      'https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop',
      'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop'
    ];

    const baseUser = {
      id: authUserId, // Usar ID de Supabase Auth
      name: registerData.name.trim(),
      age,
      location,
      images: defaultImages,
      bio: 'Profesional apasionado por la innovación y el crecimiento empresarial.',
      userType: registerData.userType,
      email: registerData.email.trim()
    };

    if (registerData.userType === 'entrepreneur') {
      const companies = ['TechFlow', 'InnovateHub', 'StartupLab', 'BusinessCore', 'VenturePoint'];
      const industries = ['SaaS', 'FinTech', 'HealthTech', 'EdTech', 'CleanTech'];
      const stages = ['idea', 'prototype', 'mvp', 'early-revenue', 'scaling'] as const;
      
      return {
        ...baseUser,
        userType: 'entrepreneur',
        company: companies[Math.floor(Math.random() * companies.length)],
        industry: industries[Math.floor(Math.random() * industries.length)],
        stage: stages[Math.floor(Math.random() * stages.length)],
        fundingNeeded: '$500K - $2M',
        previousExperience: 'Experiencia en desarrollo de productos y gestión empresarial',
        businessModel: 'B2B SaaS con modelo de suscripción',
        teamSize: Math.floor(Math.random() * 20) + 3,
        achievements: ['Producto validado en el mercado', 'Equipo sólido', 'Tracción inicial']
      } as any;
    } else {
      const investorTypes = ['angel', 'vc', 'corporate', 'family-office'] as const;
      const focusIndustries = [
        ['SaaS', 'Enterprise Software', 'B2B Tech'],
        ['FinTech', 'InsurTech', 'Financial Services'],
        ['HealthTech', 'MedTech', 'Digital Health'],
        ['CleanTech', 'Sustainability', 'Green Energy']
      ];
      
      const selectedFocus = focusIndustries[Math.floor(Math.random() * focusIndustries.length)];
      
      return {
        ...baseUser,
        userType: 'investor',
        investorType: investorTypes[Math.floor(Math.random() * investorTypes.length)],
        investmentRange: '$100K - $1M',
        focusIndustries: selectedFocus,
        investmentStage: ['Seed', 'Series A'],
        portfolioSize: Math.floor(Math.random() * 30) + 5,
        notableInvestments: ['TechStartup', 'InnovateCorp', 'GrowthVenture'],
        expertise: ['Business Strategy', 'Market Development', 'Scaling Operations']
      } as any;
    }
  }

  /**
   * Manejo local para desarrollo sin Supabase
   */
  private static async handleLocalRegistration(registerData: RegisterData): Promise<AuthResult> {
    console.log('🔄 Registro local (sin Supabase)');
    
    // Verificar si el email ya existe localmente
    const existingUsers = JSON.parse(localStorage.getItem('racksMatch_users') || '[]');
    if (existingUsers.find((u: any) => u.email === registerData.email)) {
      return { success: false, error: 'Este email ya está registrado' };
    }

    const newUser = this.createUserFromRegisterData(registerData, `local-${Date.now()}`);
    
    // Guardar en localStorage
    existingUsers.push(newUser);
    localStorage.setItem('racksMatch_users', JSON.stringify(existingUsers));
    localStorage.setItem('racksMatchUserProfile', JSON.stringify(newUser));

    return { success: true, user: newUser };
  }

  private static async handleLocalLogin(credentials: LoginCredentials): Promise<AuthResult> {
    console.log('🔄 Login local (sin Supabase)');
    
    const existingUsers = JSON.parse(localStorage.getItem('racksMatch_users') || '[]');
    const user = existingUsers.find((u: any) => u.email === credentials.email);
    
    if (!user) {
      return { success: false, error: 'Email o contraseña incorrectos' };
    }

    localStorage.setItem('racksMatchUserProfile', JSON.stringify(user));
    return { success: true, user: user };
  }
}